import { useState, useMemo } from "react"
import { Button, Row, Col, FormFeedback, Input } from "reactstrap"
import Select from "react-select"
import ComplimentaryItemBadge from "./ComplimentaryItemBadge"

const ComplimentaryItemsManager = ({
  items = [],
  availableItems = [],
  excludedNames = [],
  onAdd,
  onEdit,
  onDelete,
  onCreateOption,
  canAdd = true,
  canEdit = true,
  canDelete = true,
}) => {
  const [searchValue, setSearchValue] = useState("")
  const [selectedOption, setSelectedOption] = useState(null)
  const [customName, setCustomName] = useState("")
  const [editingItem, setEditingItem] = useState(null)
  const [editName, setEditName] = useState("")
  const [errors, setErrors] = useState({})

  const selectOptions = useMemo(() => {
    const selectedIds = (items || [])
      .map(item => item.id)
      .filter(Boolean)
      .map(id => String(id))
    const blockedNames = [
      ...(items || [])
        .map(item => item.name)
        .filter(Boolean),
      ...(excludedNames || []).filter(Boolean),
    ].map(name => String(name).trim().toLowerCase())
    return (availableItems || [])
      .filter(
        item =>
          !selectedIds.includes(String(item.id)) &&
          !blockedNames.includes(String(item.name || "").trim().toLowerCase())
      )
      .filter(item => {
        if (!searchValue) return true
        return item.name.toLowerCase().includes(searchValue.toLowerCase())
      })
      .map(item => ({
        value: item.id,
        label: item.name,
        item,
      }))
  }, [availableItems, items, excludedNames, searchValue])

  const normalizedBlockedNames = [
    ...(items || [])
      .map(item => item.name)
      .filter(Boolean),
    ...(excludedNames || []).filter(Boolean),
  ].map(name => String(name).trim().toLowerCase())

  const handleAdd = () => {
    const newErrors = {}

    if (selectedOption) {
      const alreadyAdded = items.find(i => String(i.id) === String(selectedOption.value))
      if (alreadyAdded) {
        newErrors.select = "Item already added"
        setErrors(newErrors)
        return
      }
      onAdd({ id: selectedOption.value, name: selectedOption.label })
    } else if (customName.trim()) {
      const titleCasedName = customName.trim()
        .split(/\s+/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ")
      if (normalizedBlockedNames.includes(titleCasedName.toLowerCase())) {
        newErrors.input = "Item already selected"
        setErrors(newErrors)
        return
      }
      onCreateOption?.(titleCasedName)
      onAdd({ name: titleCasedName })
    } else {
      newErrors.input = "Select an item or enter a custom name"
      setErrors(newErrors)
      return
    }

    setSelectedOption(null)
    setCustomName("")
    setErrors({})
  }

  const handleEdit = item => {
    setEditingItem(item)
    setEditName(item.custom_name || item.name)
  }

  const handleSaveEdit = () => {
    if (!editName.trim()) {
      setErrors({ edit: "Name is required" })
      return
    }
    onEdit(editingItem, editName.trim())
    setEditingItem(null)
    setEditName("")
    setErrors({})
  }

  const handleCancelEdit = () => {
    setEditingItem(null)
    setEditName("")
    setErrors({})
  }

  const handleDelete = item => {
    if (window.confirm(`Remove "${item.custom_name || item.name}"?`)) {
      onDelete(item)
    }
  }

  const showAddForm = canAdd
  const showEditActions = canEdit || canDelete

  return (
    <div className="complimentary-items-manager">
      <div className="complimentary-items-list mb-3">
        {items.length > 0 ? (
          <div className="d-flex flex-wrap gap-2">
            {items.map((item, index) => (
              <ComplimentaryItemBadge
                key={item.id || `custom-${index}`}
                item={item}
                onEdit={canEdit ? handleEdit : null}
                onDelete={canDelete ? handleDelete : null}
                showActions={showEditActions}
              />
            ))}
          </div>
        ) : (
          <div className="text-muted small">No complimentary items added</div>
        )}
      </div>

      {showAddForm && (
        <>
          {editingItem ? (
            <div className="complimentary-edit-form p-2 border rounded bg-light mb-2">
              <Row className="g-2 align-items-center">
                <Col xs={8}>
                  <Input
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    placeholder="Item name"
                    invalid={!!errors.edit}
                  />
                  {errors.edit && <FormFeedback>{errors.edit}</FormFeedback>}
                </Col>
                <Col xs={4}>
                  <div className="d-flex gap-1">
                    <Button color="success" size="sm" onClick={handleSaveEdit}>
                      <i className="bx bx-check me-1" />
                      Save
                    </Button>
                    <Button color="light" size="sm" onClick={handleCancelEdit}>
                      <i className="bx bx-x" />
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
          ) : (
            <div className="complimentary-add-form p-2 border rounded bg-light">
              <Row className="g-2 align-items-center">
                <Col md={5}>
                  <Select
                    options={selectOptions}
                    value={selectedOption}
                    onChange={option => {
                      setSelectedOption(option)
                      setCustomName("")
                      setErrors({})
                    }}
                    onInputChange={setSearchValue}
                    placeholder="Search or select item..."
                    isClearable
                    classNamePrefix="react-select"
                  />
                  {errors.select && (
                    <div className="text-danger small mt-1">{errors.select}</div>
                  )}
                </Col>
                <Col md={4}>
                  <Input
                    value={customName}
                    onChange={e => {
                      setCustomName(e.target.value)
                      setSelectedOption(null)
                      setErrors({})
                    }}
                    placeholder="Or type custom name..."
                    disabled={!!selectedOption}
                    invalid={!!errors.input}
                  />
                  {errors.input && <FormFeedback>{errors.input}</FormFeedback>}
                </Col>
                <Col md={3}>
                  <Button
                    color="primary"
                    size="sm"
                    className="w-100"
                    onClick={handleAdd}
                  >
                    <i className="bx bx-plus me-1" />
                    Add Item
                  </Button>
                </Col>
              </Row>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ComplimentaryItemsManager
